import { useEffect, useState, useCallback, useRef } from 'react';
import { MessageCircle, X, ChevronLeft } from 'lucide-react';
import {
    listChatThreads,
    schedulerGetThreadToken,
    type ChatThreadSummary,
} from '@/api/client';
import ChatPanel, { type ChatConnection } from '@/components/ChatPanel';

interface Props {
    getToken: () => Promise<string>;
    schedulerDisplayName: string;
}

const HEARTBEAT_MS = 30_000;
const THREAD_POLL_MS = 20_000;

export default function SchedulerChatWidget({ getToken, schedulerDisplayName }: Props) {
    const [open, setOpen] = useState(false);
    const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
    const [activeThread, setActiveThread] = useState<ChatThreadSummary | null>(null);
    const [connection, setConnection] = useState<ChatConnection | null>(null);
    const [connError, setConnError] = useState<string | null>(null);

    // Track last-seen message times to compute a simple unread dot.
    const seenRef = useRef<Map<number, string>>(new Map());
    const [hasUnread, setHasUnread] = useState(false);

    // Heartbeat — always running while the widget is mounted (i.e. scheduler is in the app).
    useEffect(() => {
        let active = true;
        const beat = async () => {
            try {
                const token = await getToken();
                await fetch('/api/chat/presence/heartbeat', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                });
            } catch { /* best-effort */ }
        };
        beat();
        const h = setInterval(() => { if (active) beat(); }, HEARTBEAT_MS);
        return () => { active = false; clearInterval(h); };
    }, [getToken]);

    // Poll the thread list.
    const loadThreads = useCallback(async () => {
        try {
            const token = await getToken();
            const { threads: list } = await listChatThreads(token);
            setThreads(list);
            // Unread heuristic: any thread whose LastMessageUtc is newer than what we've seen.
            let unread = false;
            for (const t of list) {
                const seen = seenRef.current.get(t.ChatThreadId);
                if (t.LastMessageUtc && (!seen || t.LastMessageUtc > seen)) {
                    if (!activeThread || activeThread.ChatThreadId !== t.ChatThreadId) unread = true;
                }
            }
            if (unread) setHasUnread(true);
        } catch { /* ignore poll errors */ }
    }, [getToken, activeThread]);

    useEffect(() => {
        loadThreads();
        const h = setInterval(loadThreads, THREAD_POLL_MS);
        return () => clearInterval(h);
    }, [loadThreads]);

    const openThread = async (t: ChatThreadSummary) => {
        setActiveThread(t);
        setConnection(null);
        setConnError(null);
        // Mark seen.
        if (t.LastMessageUtc) seenRef.current.set(t.ChatThreadId, t.LastMessageUtc);
        setHasUnread(false);
        try {
            const token = await getToken();
            const bundle = await schedulerGetThreadToken(token, t.ChatThreadId);
            setConnection({
                endpoint: bundle.endpoint,
                token: bundle.token,
                acsThreadId: bundle.acsThreadId,
                acsUserId: bundle.acsUserId,
                displayName: schedulerDisplayName,
            });
        } catch (e) {
            setConnError(e instanceof Error ? e.message : 'Could not open thread.');
        }
    };

    const backToList = () => { setActiveThread(null); setConnection(null); setConnError(null); loadThreads(); };

    /* ⭐ F223: a client's thread says so, so a scheduler knows who is on the other end */
    const partyOf = (t: ChatThreadSummary) => (t.ClientCode ? `Client ${t.ClientCode}` : (t.CarrierCode ?? 'Carrier'));
    const labelFor = (t: ChatThreadSummary) =>
        t.ScopeType === 'APPOINTMENT'
            ? `Appointment · ${t.FacilityCode}${t.ClientCode ? ` · ${partyOf(t)}` : ''}`
            : `${partyOf(t)} · ${t.FacilityCode}`;

    return (
        <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 60 }}>
            {open ? (
                <div className="bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col"
                     style={{ width: 360, height: 520, maxHeight: '80vh' }}>
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl">
                        <div className="flex items-center gap-2">
                            {activeThread && (
                                <button onClick={backToList} className="text-gray-400 hover:text-gray-700">
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                            )}
                            <span className="text-sm font-semibold text-gray-800">
                                {activeThread ? labelFor(activeThread) : 'Carrier messages'}
                            </span>
                        </div>
                        <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Body */}
                    {!activeThread ? (
                        <div className="flex-1 overflow-y-auto">
                            {threads.length === 0 ? (
                                <div className="text-center text-xs text-gray-400 py-12">No conversations yet.</div>
                            ) : (
                                threads.map(t => (
                                    <button
                                        key={t.ChatThreadId}
                                        onClick={() => openThread(t)}
                                        className="w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-blue-50"
                                    >
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-medium text-gray-800">{labelFor(t)}</span>
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                                t.ScopeType === 'APPOINTMENT' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                                {t.ScopeType === 'APPOINTMENT' ? 'Appt' : 'General'}
                                            </span>
                                        </div>
                                        {t.LastMessageUtc && (
                                            <div className="text-[11px] text-gray-400 mt-0.5">
                                                Last: {new Date(t.LastMessageUtc).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                            </div>
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    ) : (
                        <div className="flex-1 min-h-0 px-3 py-2">
                            {connError ? (
                                <div className="text-sm text-red-600 p-3">{connError}</div>
                            ) : connection ? (
                                <ChatPanel connection={connection} onMessageSent={loadThreads} />
                            ) : (
                                <div className="text-sm text-gray-400 p-3 animate-pulse">Opening…</div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <button
                    onClick={() => { setOpen(true); loadThreads(); }}
                    className="relative flex items-center justify-center w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700"
                    title="Carrier messages"
                >
                    <MessageCircle className="h-6 w-6" />
                    {hasUnread && (
                        <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-white" />
                    )}
                </button>
            )}
        </div>
    );
}
